import React, { useEffect, useMemo, useState } from "react";

import { Tabs } from "antd";
import moment from "moment";
import { Carousel } from "react-responsive-carousel";

import { SPEC_FLAGS_MAP } from "../../../../../Common/constants/SpecFlagConstants";
import { useAccessStatus } from "../../../../../grace-period/hooks/useAccessStatus";
import { getVisibleSpecFlag } from "../../../../../grace-period/utils";
// import CorrectiveActionResult from "./CorrectiveActionResult";
import ResultTable from "./ResultTable";
import ResultTableActions from "./ResultTableActions";
import UpcomingSchedulesList from "./UpcomingSchedulesList";

import { ReactComponent as StatusDotIcon } from "../../../../../../assets/images/environment/statusPingDot.svg";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import "./ResultTableCarousel.css";

export default function ResultTableCarousel({
  specsData,
  activeDate,
  report,
  upcomingDates = [],
  caTabDates = [],
  tabDateSpecFlags = {},
  setSelectedReportTabDate,
  isGeneratingPartial,
  testCheckboxVals,
  setTestCheckboxVals,
  setCarouselRetestDate,
  reportPathExists,
  getTestValGrouped,
}) {
  const { areResultsLocked } = useAccessStatus();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentArrow, setCurrentArrow] = useState(0);

  const sortedSpecData = useMemo(() => {
    if (!specsData) {
      return {
        data: [], // used for holding spec data
        tabSeq: [], // used for squencing tabs
        sliderSeq: [], // used for squencing slides for retest data pagination
        sortedSeq: [], // used for sorting dates
        tabsToShow: [], // used for showing tabs
      };
    }
    const sortedSeq = Object.keys(specsData).sort((a, b) => moment(new Date(b)).valueOf() - moment(new Date(a)).valueOf());
    const formattedActiveDate = moment(activeDate).format("MM/DD/YYYY");
    const matchingActiveDate = sortedSeq.find((date) => (
      moment(new Date(date)).format("MM/DD/YYYY") === formattedActiveDate
    )) ?? sortedSeq[0];
    let tabSeq = matchingActiveDate ? [matchingActiveDate] : [];
    const sliderSeq = [];

    sortedSeq.forEach((date) => {
      if (caTabDates.includes(date)) {
        tabSeq = [...new Set([...tabSeq, date])];
      } else {
        sliderSeq.push(date);
      }
    });

    const hasRows = (date) => Array.isArray(specsData[date]) && specsData[date].length > 0;
    const sliderSeqWithData = sliderSeq.filter(hasRows);
    const tabsToShow = tabSeq;

    if (sliderSeqWithData.length > 1) {
      tabSeq = [...new Set([...sliderSeqWithData, ...tabSeq])];
    }

    const data = tabSeq
      .map((date) => [date, specsData[date]])
      .filter(([, rows]) => Array.isArray(rows) && rows.length > 0);

    return {
      data,
      tabSeq,
      sliderSeq: sliderSeqWithData,
      sortedSeq,
      tabsToShow,
    };
  }, [specsData, activeDate, caTabDates]);

  const selectTabContent = (activeKey) => {
    const key = +activeKey;
    const date = sortedSpecData.tabSeq[key];
    setCurrentTab(key);
    if (key === 0) setCurrentArrow(0);
    // if there is more than one slider, set the current slide to the first slider for retest data pagination
    if (sortedSpecData.sliderSeq.length > 1) {
      const sliderDate = sortedSpecData.sliderSeq[0];
      const sliderIdx = sortedSpecData.tabSeq.indexOf(sliderDate);
      setCurrentSlide(sliderIdx >= 0 ? sliderIdx : 0);
    } else {
      setCurrentSlide(key);
    }
    return date;
  };

  useEffect(() => {
    selectTabContent(0);
    if (typeof setSelectedReportTabDate === "function") {
      setSelectedReportTabDate(moment(new Date(activeDate)).format("MM/DD/YYYY"));
    }
  }, [activeDate, setSelectedReportTabDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSlideChange = (change = 0) => {
    const newValue = currentArrow + change;
    if (newValue < sortedSpecData.sliderSeq.length && newValue >= 0) {
      const date = sortedSpecData.sliderSeq[newValue];
      const idx = sortedSpecData.data.findIndex(([slideDate]) => slideDate === date);
      setCurrentArrow(newValue);
      setCurrentSlide(idx >= 0 ? idx : newValue);
    }
  };

  const getSpecsFlagColor = (date) => {
    const flag = caTabDates.includes(date)
      ? tabDateSpecFlags[date]
      : report?.specs_flag;
    const { color } = SPEC_FLAGS_MAP[getVisibleSpecFlag(flag, areResultsLocked)] ?? SPEC_FLAGS_MAP.invalid;
    return color;
  };

  // const slideIsCADate = caTabDates?.includes(sortedSpecData.sortedSeq[currentSlide]);

  /**
   * Group check box value and data by date
   */
  useEffect(() => {
    if (specsData && Object.keys(testCheckboxVals).length === 0) {
      const groupedData = getTestValGrouped(specsData);
      setTestCheckboxVals(groupedData);
    }
  }, [specsData, reportPathExists]); // eslint-disable-line

  useEffect(() => {
    if (specsData && sortedSpecData?.data?.[currentSlide]) {
      const carouselDate = sortedSpecData.data[currentSlide][0];
      setCarouselRetestDate(carouselDate);
    }
  }, [currentSlide, specsData]); // eslint-disable-line

  // const renderReport = (idx, date, data) => {
  //   // corrective report spec data
  //   const isCAReport = data?.length > 0 && data?.[0]?.report_result;
  //   if (isCAReport) {
  //     return (
  //       <CorrectiveActionResult
  //         key={idx}
  //         date={date}
  //         data={data}
  //         isGeneratingPartial={isGeneratingPartial}
  //         testCheckboxVals={testCheckboxVals}
  //         setTestCheckboxVals={setTestCheckboxVals}
  //         reportPathExists={reportPathExists}
  //       />
  //     );
  //   }

  //   return (
  //     <ResultTable
  //       key={idx}
  //       date={date}
  //       data={data}
  //       isGeneratingPartial={isGeneratingPartial}
  //       testCheckboxVals={testCheckboxVals}
  //       setTestCheckboxVals={setTestCheckboxVals}
  //       reportPathExists={reportPathExists}
  //     />
  //   );
  // };

  if (!specsData) {
    return (
      <div className="env-report-view-report-section-results-table-container" />
    );
  }

  if (sortedSpecData.data && sortedSpecData.data.length > 0) {
    return (
      <div className="env-report-view-report-section-results-table-container">
        <div className="env-report-view-tab-container">
          <Tabs
            activeKey={String(currentTab)}
            onChange={(activeKey) => {
              const date = selectTabContent(activeKey);
              if (typeof setSelectedReportTabDate === "function") {
                setSelectedReportTabDate(moment(new Date(date)).format("MM/DD/YYYY"));
              }
            }}
            rootClassName="ResultTable__Tabs"
            tabBarGutter={8}
            items={sortedSpecData.tabsToShow.map((date, idx) => ({
              key: String(idx),
              label: (
                <div className="ResultTable__Tab">
                  <StatusDotIcon
                    width={20}
                    height={20}
                    fill={getSpecsFlagColor(date)}
                  />
                  <span>{moment(new Date(date)).format("DD MMM")}</span>
                </div>
              ),
            }))}
          />
          {upcomingDates.length > 0 ? (
            <UpcomingSchedulesList dates={upcomingDates} />
          ) : null}
        </div>
        <div className="env-report-view-report-section-results-table-body-wrap">
          <Carousel
            showIndicators={false}
            showThumbs={false}
            showArrows={false}
            showStatus={false}
            selectedItem={currentSlide}
            swipeable={sortedSpecData.data.length > 1}
          >
            {sortedSpecData.data.map(([date, data], idx) => (
              <ResultTable
                key={idx}
                date={date}
                data={data}
                isGeneratingPartial={isGeneratingPartial}
                testCheckboxVals={testCheckboxVals}
                setTestCheckboxVals={setTestCheckboxVals}
                reportPathExists={reportPathExists}
              />
            ))}
          </Carousel>
        </div>
        {sortedSpecData.sliderSeq.length > 1 && (
          <ResultTableActions
            currentSlide={currentArrow}
            totalSlides={sortedSpecData.sliderSeq.length}
            handleSlideChange={handleSlideChange}
          />
        )}
      </div>
    );
  }
  return (
    <div className="env-report-view-report-section-results-table-container" />
  );
}
